import React from 'react';
import findIndex from 'lodash/findIndex';
import { SunburstPoint } from 'react-vis';
import Grid from '@material-ui/core/Grid';
import { useWindowSize, useUpdateEffect } from 'react-use';
import { containercss } from 'app/components/Charts/sunburst/styles';
import { SunburstViz } from 'app/components/Charts/sunburst/common/viz';
import { SunburstChartProps } from 'app/components/Charts/sunburst/data';
import {
  BottomGridList,
  SideList,
} from 'app/components/Charts/sunburst/common/sidelist';
import { getSelectedItemData } from 'app/components/Charts/sunburst/utils';
import { getTotal } from 'app/components/Charts/sunburst/common/tooltip/utils';
import { InnerVizStat } from 'app/components/Charts/sunburst/common/innervizstat';
import { Box } from '@material-ui/core';
import { NoDataReportedMessage } from '../common/NoDataReportedMessage';

const emptyColor = '#EBEBEB';
const emptyStateData = {
  title: 'empty',
  color: emptyColor,
  children: [
    {
      title: '1',
      color: emptyColor,
      code: '1',
      size: '12394',
      children: [
        {
          title: '1.1',
          color: emptyColor,
          code: '1',
          size: '12394',
          children: [
            {
              title: '2.1',
              color: emptyColor,
              code: '2',
              size: '12394',
              children: [{}],
            },
          ],
        },
      ],
    },
    {
      title: '2',
      color: emptyColor,
      code: '2',
      size: '12394',
      children: [
        {
          title: '2.1',
          color: emptyColor,
          code: '2',
          size: '12394',
          children: [{}],
        },
      ],
    },
    {
      title: '3',
      color: emptyColor,
      code: '3',
      size: '13394',
      children: [{}],
    },
  ],
};

export function SunburstChart(props: SunburstChartProps) {
  const { width } = useWindowSize();
  const [selectedCount, setSelectedCount] = React.useState(0);
  const propsHasData = props.data.children.length > 0;
  const [localData, setLocalData] = React.useState(
    propsHasData ? props.data : emptyStateData
  );
  const [vizSize, setVizSize] = React.useState(props.vizSize || 600);

  React.useEffect(() => {
    setLocalData(
      getSelectedItemData(
        props.selected,
        propsHasData ? props.data : emptyStateData
      )
    );
  }, [props.selected, props.data]);

  useUpdateEffect(() => {
    if (localData) {
      setSelectedCount(getTotal(localData, false) as number);
    }
  }, [localData]);

  function goBack() {
    const prevIndex = findIndex(
      props.prevSelections,
      (ps: string) => ps === props.selected
    );
    props.setSelected(prevIndex < 1 ? '' : props.prevSelections[prevIndex - 1]);
    const updatedPrevSelections = [...props.prevSelections];
    updatedPrevSelections.pop();
    props.setPrevSelections(updatedPrevSelections);
  }

  function onArcClick(node: SunburstPoint) {
    if (propsHasData) {
      props.setPrevSelections([...props.prevSelections, node.title]);
      props.setSelected(node.title);
    }
  }

  React.useLayoutEffect(() => {
    if (width < 600) {
      setVizSize(width - 24);
    }
  }, [width]);

  function normalizeData(data: any) {
    return {
      title: propsHasData ? 'activities' : 'empty',
      color: '',
      children: data.children.map((child: any) => {
        let updChild: any = {
          code: child.code,
          title: child.title,
          color: child.color,
        };
        if (child.children) {
          updChild = {
            ...updChild,
            children: child.children,
            size: child.size,
          };
        } else {
          updChild = {
            ...updChild,
            size: child.size,
            children: [],
          };
        }
        return updChild;
      }),
    };
  }

  return (
    <Grid container>
      <Box height="40px" width="100%" />
      {!props.showBottomGridList && (
        <Grid
          item
          md={12}
          lg={3}
          css={`
            @media (max-width: 960px) {
              order: 2;
            }
          `}
        >
          <SideList
            data={localData}
            selected={props.selected}
            title="Sectors"
          />
        </Grid>
      )}
      <Grid
        item
        md={12}
        lg={!props.showBottomGridList ? 9 : 12}
        css={`
          @media (max-width: 960px) {
            order: 1;
            flex-basis: 100%;
            margin-bottom: 24px;
          }
        `}
      >
        <div
          css={containercss}
          id="sunburst-container"
          style={{ height: vizSize }}
        >
          <InnerVizStat
            goBack={goBack}
            showGoBackBtn={props.prevSelections.length > 0}
            count={
              props.selected !== '' ? selectedCount : props.activitiesCount
            }
          />
          <SunburstViz
            aria-label="Sunburst chart showing total budget divided by sectors."
            size={vizSize}
            setSelected={onArcClick}
            data={normalizeData(localData)}
            setSelectedCount={setSelectedCount}
          />
        </div>
        {!propsHasData && <NoDataReportedMessage />}
      </Grid>
      {props.showBottomGridList && propsHasData && (
        <Grid item sm={12} md={12} lg={12}>
          <BottomGridList
            data={localData}
            selected={props.selected}
            title="Sector categories"
          />
        </Grid>
      )}
    </Grid>
  );
}
